import ReactEChartsCore from 'echarts-for-react/lib/core';
import { ChartContainer } from '../../analytics/shared/ChartContainer';
import { EmptyState } from '../../analytics/shared/EmptyState';
import { echarts } from '../../analytics/shared/echarts-config';
import { buildHorizontalBarOptions } from '../../analytics/shared/chart-option-builders';
import type {
  EngagementMetricsCategory,
  EngagementMetricsTopProvider,
} from '../../../types/engagement-metrics.types';

type ProviderCategoryBreakdownsProps = {
  topProviders: EngagementMetricsTopProvider[];
  byCategory: EngagementMetricsCategory[];
};

const buildCategoryOptions = (data: EngagementMetricsCategory[]) =>
  buildHorizontalBarOptions(
    data.map((item) => ({ label: item.categoryName, count: item.count })),
    110,
    '#5d8b76',
  );

const TopProviders = ({ providers }: { providers: EngagementMetricsTopProvider[] }) => {
  if (providers.length === 0) {
    return <EmptyState message="No provider engagement data available." />;
  }

  const maxCount = providers[0]?.count ?? 1;
  return (
    <ol className="space-y-3">
      {providers.map((provider, index) => (
        <li
          key={provider.providerUserId}
          className="grid grid-cols-[24px_1fr_auto] items-center gap-3 border-b pb-3 last:border-0"
          style={{ borderColor: '#edeae3' }}
        >
          <span className="text-xs font-bold" style={{ color: '#7f8a83' }}>
            {index + 1}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold" style={{ color: '#1b2a23' }}>
              {provider.providerName}
            </p>
            <p className="truncate text-[11px]" style={{ color: '#7f8a83' }}>
              {provider.companyName ?? 'Independent provider'}
            </p>
            <div className="mt-1 h-1.5 rounded-full" style={{ backgroundColor: '#edeae3' }}>
              <div
                className="h-1.5 rounded-full"
                style={{
                  backgroundColor: '#2f7d5e',
                  width: `${Math.round((provider.count / maxCount) * 100)}%`,
                }}
              />
            </div>
          </div>
          <span className="text-[15px] font-extrabold tabular-nums" style={{ color: '#15543e' }}>
            {provider.count}
          </span>
        </li>
      ))}
    </ol>
  );
};

export const ProviderCategoryBreakdowns = ({
  topProviders,
  byCategory,
}: ProviderCategoryBreakdownsProps) => (
  <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
    <ChartContainer
      title="Top Providers by Engagements"
      subtitle="Providers ranked by qualifying engagement volume"
    >
      <TopProviders providers={topProviders} />
    </ChartContainer>

    <ChartContainer title="By Category" subtitle="Selected discovery categories">
      {byCategory.length > 0 ? (
        <ReactEChartsCore
          echarts={echarts}
          option={buildCategoryOptions(byCategory)}
          style={{ height: 300 }}
          notMerge
          lazyUpdate
        />
      ) : (
        <EmptyState message="No category breakdown data available." />
      )}
    </ChartContainer>
  </div>
);
